import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { MaskedLine, FadeUp, Page, Meta } from "../components/Reveal";
import { getArticles } from "../lib/api";
import { useContent } from "../lib/ContentContext";

const CATEGORIES = ["Toutes", "Sexualité", "Relations", "Intimité", "Bien-être", "Société & innovation"];

export default function Conversations() {
  const c = useContent();
  const SERIES = [1, 2, 3, 4, 5].map((n) => ({
    title: c(`conversations.serie.${n}.titre`),
    text: c(`conversations.serie.${n}.texte`),
  }));
  const [filter, setFilter] = useState("Toutes");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    getArticles()
      .then(setArticles)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const visible = filter === "Toutes" ? articles : articles.filter((a) => a.cat === filter);

  return (
    <Page testId="conversations-page">
      <section className="mx-auto max-w-7xl px-6 pt-36 md:px-12 md:pt-48">
        <FadeUp><Meta>{c("conversations.hero.meta")}</Meta></FadeUp>
        <h1 className="mt-6 font-serif text-5xl font-light leading-[1.08] tracking-tight md:text-7xl">
          <MaskedLine delay={0.2}>{c("conversations.hero.t1")}<em className="text-clay">{c("conversations.hero.t2")}</em></MaskedLine>
        </h1>
        <FadeUp delay={0.5} className="mt-10 max-w-2xl">
          <p className="text-base leading-relaxed text-smoke md:text-lg">
            {c("conversations.hero.p")}
          </p>
        </FadeUp>
      </section>

      <section data-testid="series-section" className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28">
        <FadeUp><Meta>{c("conversations.series.meta")}</Meta></FadeUp>
        <div className="mt-10 grid gap-px overflow-hidden rounded-[1.75rem] bg-linen sm:grid-cols-2 lg:grid-cols-5">
          {SERIES.map((s, i) => (
            <FadeUp key={s.title} delay={0.06 * i} className="h-full">
              <div data-testid={`serie-card-${i + 1}`} className="flex h-full flex-col bg-cream p-7 transition-colors duration-500 hover:bg-sand/30">
                <h3 className="font-serif text-xl font-light leading-snug tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-smoke">{s.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="articles-section" className="mx-auto max-w-7xl px-6 pb-24 md:px-12 md:pb-36">
        <FadeUp>
          <div data-testid="category-filters" className="flex flex-wrap gap-3">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`rounded-full border px-5 py-2 text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-300 ${
                  filter === c
                    ? "border-clay bg-clay text-cream"
                    : "border-linen text-smoke hover:border-clay hover:text-clay"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </FadeUp>

        {loading ? (
          <div className="flex justify-center py-24" data-testid="articles-loading">
            <Loader2 className="h-6 w-6 animate-spin text-clay" />
          </div>
        ) : error ? (
          <p data-testid="articles-error" className="mt-14 rounded-[1.75rem] border border-dashed border-linen py-16 text-center font-serif text-xl font-light italic text-smoke">
            Les conversations n'ont pas pu être chargées. Réessayez dans un instant.
          </p>
        ) : visible.length === 0 ? (
          <p data-testid="articles-empty" className="mt-14 rounded-[1.75rem] border border-dashed border-linen py-16 text-center font-serif text-xl font-light italic text-smoke">
            Aucune conversation dans cette catégorie pour l'instant.
          </p>
        ) : (
          <div className="mt-14 grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((a, i) => (
              <FadeUp key={a.slug} delay={0.05 * i} className="h-full">
                <Link
                  to={`/conversations/${a.slug}`}
                  data-testid={`article-card-${i + 1}`}
                  className="group flex h-full flex-col"
                >
                  {a.img && (
                    <div className="aspect-[16/10] overflow-hidden rounded-[1.75rem]">
                      <img src={a.img} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                  )}
                  <p className="mt-5 text-xs font-medium uppercase tracking-[0.2em] text-clay">{a.serie}</p>
                  <h2 className="mt-3 flex-1 font-serif text-xl font-light leading-snug tracking-tight transition-colors duration-300 group-hover:text-clay md:text-2xl">
                    {a.title}
                  </h2>
                  <div className="mt-4 flex items-center justify-between">
                    <p className="text-xs text-ash">{a.cat}{a.time ? ` · ${a.time} de lecture` : ""}</p>
                    <ArrowRight className="h-4 w-4 text-ash transition-all duration-300 group-hover:translate-x-1 group-hover:text-clay" />
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        )}
      </section>
    </Page>
  );
}
